import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { cloneDeep, debounce } from 'lodash'
import { APP_ENV } from 'shared/config'
import { logger } from 'shared/lib'
import { dataLogApi } from 'entities/DataLog/api/dataLogApi'
import { type ILog, type IParamSendLogs } from 'entities/DataLog/api/types'
import { getVersionDesign } from 'entities/Settings/lib/setVersionDesign'

export class DataLogStore<V> {
  private _disposeHasItems: IReactionDisposer | null = null
  private _itemsMap: Map<string, IParamSendLogs<V>> = new Map()
  private _limit = 50
  private _debounceSendLogs: ReturnType<typeof debounce>

  constructor() {
    makeAutoObservable(this)

    this._debounceSendLogs = debounce(this.sendLogs, 5000)

    this.reactionHasItems()
  }

  get versionOfDesign() {
    return getVersionDesign()
  }

  get source() {
    return `${APP_ENV}-web-app-${this.versionOfDesign}`
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get isFull() {
    return this.items.length >= this._limit
  }

  reactionHasItems = () => {
    this._disposeHasItems?.()
    this._disposeHasItems = reaction(
      () => this.items.length,
      () => {
        if (this.isFull) {
          this._debounceSendLogs?.cancel()
          this.sendLogs()
        } else {
          this._debounceSendLogs()
        }
      }
    )
  }

  setItem = ({ logLevel, details, message }: ILog<V>) => {
    const data: IParamSendLogs<V> = {
      timestamp: new Date().toISOString(),
      source: this.source,
      message,
      logLevel,
      details,
    }

    this._itemsMap.set(nanoid(), data)
  }

  sendLogs = async () => {
    if (!this.items.length) return
    const storedItems = cloneDeep(this.items)
    this._itemsMap.clear()

    try {
      await dataLogApi.sendLogs({ data: storedItems })
    } catch (e) {
      logger.error(e)
    }
  }
}
